<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import axios from '../axios.js'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'update:show'])

const mappings = ref([])
const loading = ref(false)
const error = ref(null)
const searchQuery = ref('')
const editingId = ref(null)
const showAddForm = ref(false)

// Form state
const formData = ref({
  slug: '',
  realm_league_id: '',
  realm_league_name: '',
  realm_path: ''
})

// Filtered mappings
const filteredMappings = computed(() => {
  if (!searchQuery.value.trim()) {
    return mappings.value
  }
  const query = searchQuery.value.toLowerCase()
  return mappings.value.filter(mapping => 
    mapping.slug?.toLowerCase().includes(query) ||
    mapping.realm_league_name?.toLowerCase().includes(query) ||
    mapping.realm_path?.toLowerCase().includes(query) ||
    String(mapping.realm_league_id).includes(query)
  )
})

// Fetch all mappings
const fetchMappings = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await axios.get('/api/realm-leagues-mapping')
    mappings.value = response.data || []
  } catch (err) {
    error.value = 'Failed to load mappings'
    console.error('Error fetching mappings:', err)
  } finally {
    loading.value = false
  }
}

// Reset form
const resetForm = () => {
  formData.value = {
    slug: '',
    realm_league_id: '',
    realm_league_name: '',
    realm_path: ''
  }
  editingId.value = null
  showAddForm.value = false
}

// Start editing
const startEdit = (mapping) => {
  editingId.value = mapping.id
  formData.value = {
    slug: mapping.slug,
    realm_league_id: mapping.realm_league_id,
    realm_league_name: mapping.realm_league_name,
    realm_path: mapping.realm_path
  }
  showAddForm.value = true
}

// Cancel edit/add
const cancelEdit = () => {
  resetForm()
}

// Save mapping (create or update)
const saveMapping = async () => {
  if (!formData.value.slug || !formData.value.realm_league_id || 
      !formData.value.realm_league_name || !formData.value.realm_path) {
    error.value = 'Please fill in all fields'
    return
  }

  loading.value = true
  error.value = null

  try {
    if (editingId.value) {
      // Update existing
      await axios.put(`/api/realm-leagues-mapping/${editingId.value}`, formData.value)
    } else {
      // Create new
      await axios.post('/api/realm-leagues-mapping', formData.value)
    }
    
    await fetchMappings()
    resetForm()
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to save mapping'
    console.error('Error saving mapping:', err)
  } finally {
    loading.value = false
  }
}

// Delete mapping
const deleteMapping = async (id) => {
  if (!confirm('Are you sure you want to delete this mapping?')) {
    return
  }

  loading.value = true
  error.value = null

  try {
    await axios.delete(`/api/realm-leagues-mapping/${id}`)
    await fetchMappings()
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to delete mapping'
    console.error('Error deleting mapping:', err)
  } finally {
    loading.value = false
  }
}

// Close overlay
const closeOverlay = () => {
  emit('update:show', false)
  emit('close')
  resetForm()
}

// Watch for show prop changes
watch(() => props.show, (isOpen) => {
  if (isOpen) {
    fetchMappings()
    resetForm()
  }
})
</script>

<template>
  <div 
    v-if="show" 
    class="fixed inset-0 bg-black/60 z-[9998] flex items-center justify-center p-4"
    data-no-global-context-menu
  >
    <div class="absolute inset-0" @click="closeOverlay"></div>
    <div class="relative z-10 bg-white rounded-lg shadow-2xl w-full max-w-[95vw] h-[90vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <div class="flex items-center space-x-3">
          <i class="fas fa-basketball-ball text-pink-500 text-xl"></i>
          <div>
            <div class="text-lg font-semibold text-gray-800">RealGM Leagues Mapping</div>
            <div class="text-xs text-gray-500">Manage league mappings for RealGM scraper</div>
          </div>
        </div>
        <button 
          class="p-2 rounded-full hover:bg-gray-100 text-gray-500" 
          @click="closeOverlay"
          title="Close"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Search and Add Button -->
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4 flex-shrink-0">
        <div class="flex-1 relative">
          <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          <input 
            v-model="searchQuery"
            type="text"
            class="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-pink-500"
            placeholder="Search by slug, name, path, or ID..."
          />
        </div>
        <button
          @click="showAddForm = !showAddForm; editingId = null; if (!showAddForm) resetForm()"
          class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors flex items-center gap-2"
        >
          <i class="fas fa-plus"></i>
          {{ showAddForm && !editingId ? 'Cancel' : 'Add New' }}
        </button>
      </div>

      <!-- Add/Edit Form -->
      <div v-if="showAddForm" class="px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <div class="bg-white rounded-lg p-4 shadow-sm">
          <h3 class="text-sm font-semibold mb-4 text-gray-700">
            {{ editingId ? 'Edit Mapping' : 'Add New Mapping' }}
          </h3>
          
          <div v-if="error" class="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {{ error }}
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs text-gray-600 mb-1">Slug *</label>
              <input
                v-model="formData.slug"
                type="text"
                class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-pink-500"
                placeholder="e.g., adriatic-league"
              />
            </div>
            <div>
              <label class="block text-xs text-gray-600 mb-1">RealGM League ID *</label>
              <input
                v-model.number="formData.realm_league_id"
                type="number"
                class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-pink-500"
                placeholder="e.g., 18"
              />
            </div>
            <div>
              <label class="block text-xs text-gray-600 mb-1">RealGM League Name *</label>
              <input
                v-model="formData.realm_league_name"
                type="text"
                class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-pink-500"
                placeholder="e.g., Adriatic League Liga ABA"
              />
            </div>
            <div>
              <label class="block text-xs text-gray-600 mb-1">RealGM Path *</label>
              <input
                v-model="formData.realm_path"
                type="text"
                class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-pink-500"
                placeholder="e.g., international/league/18/Adriatic-League-Liga-ABA"
              />
            </div>
          </div>

          <div class="flex justify-end gap-2 mt-4">
            <button
              @click="cancelEdit"
              class="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              @click="saveMapping"
              :disabled="loading"
              class="px-4 py-2 text-sm bg-pink-500 text-white rounded hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ loading ? 'Saving...' : (editingId ? 'Update' : 'Create') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-auto p-6">
        <div v-if="loading && mappings.length === 0" class="text-center py-8">
          <i class="fas fa-spinner fa-spin text-2xl text-gray-400 mb-2"></i>
          <p class="text-sm text-gray-500">Loading mappings...</p>
        </div>
        
        <div v-else-if="error && mappings.length === 0" class="text-center py-8">
          <i class="fas fa-exclamation-circle text-2xl text-red-400 mb-2"></i>
          <p class="text-sm text-red-600">{{ error }}</p>
          <button
            @click="fetchMappings"
            class="mt-4 px-4 py-2 text-sm bg-pink-500 text-white rounded hover:bg-pink-600"
          >
            Retry
          </button>
        </div>

        <div v-else-if="!filteredMappings.length" class="text-center py-8">
          <i class="fas fa-inbox text-4xl text-gray-300 mb-2"></i>
          <p class="text-sm text-gray-500">
            {{ searchQuery ? 'No mappings found matching your search' : 'No mappings available' }}
          </p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-gray-100 text-gray-700 uppercase text-xs">
              <tr>
                <th class="px-4 py-3 text-left">ID</th>
                <th class="px-4 py-3 text-left">Slug</th>
                <th class="px-4 py-3 text-left">RealGM League ID</th>
                <th class="px-4 py-3 text-left">League Name</th>
                <th class="px-4 py-3 text-left">Path</th>
                <th class="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr
                v-for="mapping in filteredMappings"
                :key="mapping.id"
                class="hover:bg-gray-50"
              >
                <td class="px-4 py-3 font-medium text-gray-900">
                  {{ mapping.id }}
                </td>
                <td class="px-4 py-3">
                  <code class="text-xs bg-gray-100 px-2 py-1 rounded">{{ mapping.slug }}</code>
                </td>
                <td class="px-4 py-3 text-gray-700">
                  {{ mapping.realm_league_id }}
                </td>
                <td class="px-4 py-3 text-gray-700">
                  {{ mapping.realm_league_name }}
                </td>
                <td class="px-4 py-3">
                  <code class="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
                    {{ mapping.realm_path }}
                  </code>
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center gap-2">
                    <button
                      @click="startEdit(mapping)"
                      class="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button
                      @click="deleteMapping(mapping.id)"
                      class="p-1.5 text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
        <div class="text-xs text-gray-500">
          Total: {{ mappings.length }} mapping{{ mappings.length !== 1 ? 's' : '' }}
          <span v-if="searchQuery && filteredMappings.length !== mappings.length">
            ({{ filteredMappings.length }} filtered)
          </span>
        </div>
        <button
          @click="fetchMappings"
          :disabled="loading"
          class="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
        >
          <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': loading }"></i>
          Refresh
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
code {
  font-family: 'Courier New', monospace;
}
</style>
